import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  MessageCircle,
  Send,
  CheckCircle,
  AlertCircle,
  FileText,
  Clock,
  User,
  RefreshCw,
  Download,
  Hash
} from "lucide-react";

export default function OwnerChat() {
  const [messages, setMessages] = useState([]);
  const [pendingQueries, setPendingQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyDialog, setReplyDialog] = useState({ open: false, order: null });
  const [replyMessage, setReplyMessage] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({ open: false, order: null });
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchData();
    // Poll for new messages
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [messagesRes, pendingRes] = await Promise.all([
        axios.get(`${API}/owner/messages`),
        axios.get(`${API}/owner/pending-queries`)
      ]);
      setMessages(messagesRes.data);
      setPendingQueries(pendingRes.data);
    } catch (error) {
      console.error("Failed to fetch owner data");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmOrder = async () => {
    if (!confirmationNumber.trim() || !confirmDialog.order) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/orders/${confirmDialog.order.id}/confirm-with-number?confirmation_number=${encodeURIComponent(confirmationNumber)}`
      );
      toast.success("Order confirmed and notification sent to customer");
      setConfirmDialog({ open: false, order: null });
      setConfirmationNumber("");
      fetchData();
    } catch (error) {
      toast.error("Failed to confirm order");
    } finally {
      setSending(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !replyDialog.order) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/owner/reply?order_number=${encodeURIComponent(replyDialog.order.order_number)}&reply_message=${encodeURIComponent(replyMessage)}`
      );
      toast.success("Reply sent to customer");
      setReplyDialog({ open: false, order: null });
      setReplyMessage("");
      fetchData();
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const downloadProforma = async (orderId, orderNumber) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/proforma-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `proforma_${orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download proforma");
    }
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'proforma': return <FileText className="w-4 h-4 text-blue-500" />;
      case 'query': return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      case 'confirmation': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'response': return <Send className="w-4 h-4 text-purple-500" />;
      default: return <MessageCircle className="w-4 h-4" />;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Group messages by order
  const groupedMessages = messages.reduce((acc, msg) => {
    const key = msg.order_number || 'general';
    if (!acc[key]) acc[key] = [];
    acc[key].push(msg);
    return acc;
  }, {});

  // Get orders awaiting confirmation (proforma sent but not confirmed)
  const awaitingConfirmation = messages.filter(
    m => m.message_type === 'proforma' && m.from_bot
  ).reduce((acc, msg) => {
    if (!acc.some(m => m.order_number === msg.order_number)) {
      acc.push(msg);
    }
    return acc;
  }, []);

  return (
    <div className="space-y-6">
      {/* Pending Actions */}
      {(pendingQueries.length > 0 || awaitingConfirmation.length > 0) && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-yellow-800" style={{ fontFamily: 'Manrope' }}>
              <AlertCircle className="w-5 h-5" />
              Pending Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Awaiting Confirmation */}
            {awaitingConfirmation.slice(0, 5).map((msg) => (
              <div key={msg.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-yellow-200">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="font-medium">Order {msg.order_number}</p>
                    <p className="text-sm text-muted-foreground">Proforma sent - awaiting confirmation number</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => downloadProforma(msg.order_id, msg.order_number)}>
                    <Download className="w-4 h-4 mr-1" /> PDF
                  </Button>
                  <Button size="sm" onClick={() => setConfirmDialog({ open: true, order: { id: msg.order_id, order_number: msg.order_number } })}>
                    <Hash className="w-4 h-4 mr-1" /> Confirm
                  </Button>
                </div>
              </div>
            ))}
            
            {/* Pending Queries */}
            {pendingQueries.map((order) => (
              <div key={order.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-yellow-200">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-500" />
                  <div>
                    <p className="font-medium">Order {order.order_number}</p>
                    <p className="text-sm text-muted-foreground">{order.pending_customer_query}</p>
                  </div>
                </div>
                <Button size="sm" onClick={() => setReplyDialog({ open: true, order })}>
                  <Send className="w-4 h-4 mr-1" /> Reply
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Message History */}
      <Card className="border-border/60">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <MessageCircle className="w-5 h-5 text-primary" />
                Owner Chat History
              </CardTitle>
              <CardDescription>
                Communication log between bot and owner
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-muted-foreground">Loading messages...</p>
              </div>
            ) : messages.length > 0 ? (
              <div className="space-y-4">
                {messages.slice(0, 50).map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`p-4 rounded-lg ${msg.from_bot ? 'bg-secondary/50' : 'bg-primary/5 border border-primary/20'}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getMessageIcon(msg.message_type)}
                        <Badge variant="outline" className="font-mono text-xs">
                          {msg.order_number}
                        </Badge>
                        <Badge className={msg.from_bot ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
                          {msg.from_bot ? 'Bot' : 'Owner'}
                        </Badge>
                      </div>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    
                    {msg.message_type === 'proforma' && msg.from_bot && (
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => downloadProforma(msg.order_id, msg.order_number)}>
                          <Download className="w-4 h-4 mr-1" /> Download PDF
                        </Button>
                        <Button size="sm" onClick={() => setConfirmDialog({ open: true, order: { id: msg.order_id, order_number: msg.order_number } })}>
                          <Hash className="w-4 h-4 mr-1" /> Enter Confirmation #
                        </Button>
                      </div>
                    )}
                    
                    {msg.message_type === 'query' && msg.from_bot && (
                      <Button size="sm" className="mt-3" onClick={() => setReplyDialog({ open: true, order: { order_number: msg.order_number, customer_phone: msg.customer_phone } })}>
                        <Send className="w-4 h-4 mr-1" /> Reply to Customer
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <MessageCircle className="w-12 h-12 mb-4 opacity-30" />
                <p>No messages yet</p>
                <p className="text-sm">Messages will appear when orders are confirmed</p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ open, order: confirmDialog.order })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Enter Order Confirmation Number</DialogTitle>
            <DialogDescription>
              Enter the confirmation number for Order {confirmDialog.order?.order_number}. This will be sent to the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Input
              placeholder="e.g., CONF-2024-001"
              value={confirmationNumber}
              onChange={(e) => setConfirmationNumber(e.target.value)}
              data-testid="confirmation-number-input"
            />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setConfirmDialog({ open: false, order: null })}>
                Cancel
              </Button>
              <Button onClick={handleConfirmOrder} disabled={sending || !confirmationNumber.trim()}>
                {sending ? "Sending..." : "Confirm & Notify Customer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialog.open} onOpenChange={(open) => setReplyDialog({ open, order: replyDialog.order })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Reply to Customer Query</DialogTitle>
            <DialogDescription>
              Your reply will be sent to the customer for Order {replyDialog.order?.order_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Textarea
              placeholder="Type your reply..."
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              rows={4}
              data-testid="reply-message-input"
            />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setReplyDialog({ open: false, order: null })}>
                Cancel
              </Button>
              <Button onClick={handleSendReply} disabled={sending || !replyMessage.trim()}>
                {sending ? "Sending..." : "Send Reply"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
